import type {
  AttachmentView,
  MessageView,
  PublicPresence,
  RealtimeTicketResponse,
  RedLineAvailabilityState,
  RedLineMessagesResponse,
  RedLineSessionState,
  RedLineSignalReaction,
  SignalPortalConfig,
  SignalPortalConfigUpdate,
  SignalPortalStatusUpdate,
} from '@deffy-direct/contracts';

const configuredApiBaseUrl: unknown = import.meta.env.VITE_API_BASE_URL;
export const API_BASE_URL = (
  typeof configuredApiBaseUrl === 'string' ? configuredApiBaseUrl : 'http://localhost:8787/api/v1'
).replace(/\/$/u, '');

let csrfToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: () => void): void {
  unauthorizedHandler = handler;
}

export class AdminApiFailure extends Error {
  readonly code: string;
  readonly status: number;
  readonly details: unknown;

  constructor(message: string, code: string, status: number, details?: unknown) {
    super(message);
    this.name = 'AdminApiFailure';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set('Accept', 'application/json');
  if (options.body) headers.set('Content-Type', 'application/json');
  if (options.method && options.method !== 'GET' && options.method !== 'HEAD' && csrfToken) {
    headers.set('X-CSRF-Token', csrfToken);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      ...options,
      headers,
      credentials: 'include',
    });
  } catch (error) {
    throw new AdminApiFailure('The admin relay is unreachable.', 'NETWORK_UNAVAILABLE', 0, error);
  }

  const payload = (await response.json().catch(() => ({}))) as T & {
    error?: { code?: string; message?: string; details?: unknown };
  };
  if (!response.ok) {
    if (response.status === 401 && path !== '/admin/auth/login') unauthorizedHandler?.();
    throw new AdminApiFailure(
      payload.error?.message ?? 'The admin relay rejected this action.',
      payload.error?.code ?? 'ADMIN_API_ERROR',
      response.status,
      payload.error?.details,
    );
  }
  return payload;
}

export interface Overview {
  presence: PublicPresence;
  counts: {
    pendingRequests: number;
    observedRequests: number;
    acceptedRequests: number;
    activeConversations: number;
    pausedConversations: number;
    unreadMessages: number;
    openBugReports: number;
    quarantinedAttachments: number;
  };
}

export interface RequestListItem {
  publicId: string;
  state: string;
  alias: string;
  intent: string | null;
  urgency: 'casual' | 'normal' | 'important';
  sourceProject: { id: string; label: string };
  firstMessageExcerpt: string;
  messageCount: number;
  attachmentCount: number;
  pendingAdditionCount: number;
  returningVisitor: boolean;
  waitingSince: string;
  expiresAt: string;
  actualQueuePosition: number;
  publicEstimatedPosition: number;
  priority: number;
  pinned: boolean;
  aiSummary: null;
  aiSummaryState: 'unavailable';
}

export interface RequestDetail {
  publicId: string;
  state: string;
  alias: string;
  identity: {
    publicId: string;
    mode: string;
    createdAt: string;
    returningVisitor: boolean;
  };
  intent: string | null;
  urgency: string;
  sourceProject: { id: string; label: string };
  createdAt: string;
  expiresAt: string;
  priority: number;
  pinned: boolean;
  publicEstimatedPosition: number | null;
  aiSummary: null;
  aiSummaryState: 'unavailable';
  messages: MessageView[];
  attachments: Array<{
    publicId: string;
    filename: string;
    mime: string;
    sizeBytes: number;
    validationState: string;
    sanitizationState: string;
    scanState: string;
    kind: AttachmentView['kind'];
    width: number | null;
    height: number | null;
    durationMs: number | null;
    waveform: number[] | null;
    transcript: AttachmentView['transcript'];
    processingErrorCode: string | null;
  }>;
}

export interface ConversationListItem {
  publicId: string;
  alias: string;
  state: 'active' | 'paused' | 'blocked';
  accent: string;
  lastMessageAt: string;
  lastMessageExcerpt: string | null;
  unreadCount: number;
  pauseReason: string | null;
  expectedReturn: string | null;
}

export interface AdminConversation {
  publicId: string;
  state: 'active' | 'paused' | 'blocked';
  accent: string;
  alias: string;
  urgency: string;
  intent: string | null;
  sourceProjectId: string | null;
  pausedReason: string | null;
  expectedReturn: string | null;
  unreadCount: number;
  messages: MessageView[];
}

export interface BugReportItem {
  publicId: string;
  description: string;
  expectedBehavior: string | null;
  actualBehavior: string | null;
  severity: string;
  reproduction: string | null;
  replyPreference: 'none' | 'if_needed' | 'conversation';
  diagnosticConsent: boolean;
  diagnostics: {
    basic: {
      sourceSite?: string | null;
      currentUrl?: string | null;
      projectIdentifier: string;
      buildVersion?: string | null;
      browserFamily: string;
      operatingSystem: string;
      viewportCategory: string;
      referrer?: string | null;
      timestamp: string;
    } | null;
    detailed: {
      systemState?: {
        online: boolean;
        language: string;
        colorScheme: string;
        reducedMotion: boolean;
        pixelRatioCategory: string;
        navigationType: string;
      };
      consoleLogs?: string[];
      recentApplicationEvents?: string[];
      networkErrors?: string[];
      reproductionTrace?: string[];
    } | null;
    expiresAt: string | null;
    available: boolean;
  };
  status: 'new' | 'triaged' | 'in_progress' | 'resolved' | 'closed';
  linkedConversationPublicId: string | null;
  evidence: AttachmentView[];
  project: { id: string; label: string };
  createdAt: string;
  updatedAt: string;
}

export const adminApi = {
  async login(password: string) {
    const result = await request<{
      authenticated: true;
      session: { csrfToken: string; idleExpiresAt: string; absoluteExpiresAt: string };
    }>('/admin/auth/login', { method: 'POST', body: JSON.stringify({ password }) });
    csrfToken = result.session.csrfToken;
    return result;
  },
  async session() {
    const result = await request<{
      authenticated: true;
      session: {
        csrfToken: string;
        createdAt: string;
        idleExpiresAt: string;
        absoluteExpiresAt: string;
      };
    }>('/admin/auth/session');
    csrfToken = result.session.csrfToken;
    return result;
  },
  async logout() {
    const result = await request<{ authenticated: false }>('/admin/auth/logout', {
      method: 'POST',
    });
    csrfToken = null;
    return result;
  },
  overview: () => request<Overview>('/admin/overview'),
  requests: (status = 'pending') =>
    request<{ requests: RequestListItem[] }>(
      `/admin/requests?status=${encodeURIComponent(status)}`,
    ),
  request: (publicId: string) =>
    request<{ request: RequestDetail }>(`/admin/requests/${encodeURIComponent(publicId)}`),
  previewRequest: (publicId: string) =>
    request<{ request: RequestDetail }>(`/admin/requests/${encodeURIComponent(publicId)}/preview`, {
      method: 'POST',
    }),
  acknowledgeRequest: (publicId: string) =>
    request<{ acknowledged: boolean; publicLabel: string }>(
      `/admin/requests/${encodeURIComponent(publicId)}/acknowledge`,
      { method: 'POST' },
    ),
  acceptRequest: (publicId: string) =>
    request<{ conversation: { publicId: string }; duplicate: boolean }>(
      `/admin/requests/${encodeURIComponent(publicId)}/accept`,
      { method: 'POST' },
    ),
  rejectRequest: (publicId: string, reasonId: string) =>
    request<{ rejected: boolean; duplicate: boolean }>(
      `/admin/requests/${encodeURIComponent(publicId)}/reject`,
      { method: 'POST', body: JSON.stringify({ reasonId }) },
    ),
  updatePriority: (
    publicId: string,
    input: { priority: number; pinned?: boolean; publicEstimatedRank?: number | null },
  ) =>
    request<{ updated: boolean }>(`/admin/requests/${encodeURIComponent(publicId)}/priority`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  rejectionPresets: () =>
    request<{
      presets: Array<{
        id: string;
        label: string;
        description: string;
        animationKey: string;
      }>;
    }>('/admin/rejection-presets'),
  updatePresence: (input: {
    preset: 'very_botherable' | 'building' | 'slow_replies' | 'sleeping';
    subtitle?: string | null;
    expiresAt?: string | null;
    fallbackPreset?: 'very_botherable' | 'building' | 'slow_replies' | 'sleeping';
    privacyMode?: boolean;
  }) =>
    request<{ presence: PublicPresence }>('/admin/presence', {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  conversations: () => request<{ conversations: ConversationListItem[] }>('/admin/conversations'),
  conversation: (publicId: string) =>
    request<{ conversation: AdminConversation }>(
      `/admin/conversations/${encodeURIComponent(publicId)}`,
    ),
  observeConversation: (publicId: string) =>
    request<{ observed: true; observedAt: string; updatedCount: number }>(
      `/admin/conversations/${encodeURIComponent(publicId)}/observe`,
      { method: 'POST' },
    ),
  realtimeTicket: (publicId: string) =>
    request<{ realtime: RealtimeTicketResponse }>(
      `/admin/conversations/${encodeURIComponent(publicId)}/realtime-ticket`,
      { method: 'POST' },
    ),
  sendMessage: (publicId: string, message: string, clientMessageId: string) =>
    request<{ message: { publicId: string; duplicate: boolean } }>(
      `/admin/conversations/${encodeURIComponent(publicId)}/messages`,
      { method: 'POST', body: JSON.stringify({ message, clientMessageId }) },
    ),
  pauseConversation: (
    publicId: string,
    input: { paused: boolean; reason?: string | null; expectedReturn?: string | null },
  ) =>
    request<{ state: string; pausedReason: string | null; expectedReturn: string | null }>(
      `/admin/conversations/${encodeURIComponent(publicId)}/pause`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  sendSignal: (
    publicId: string,
    signal: 'seen' | 'hold_on' | 'running_out_of_time' | 'patience_running_out',
  ) =>
    request<{ signal: { publicId: string; type: string } }>(
      `/admin/conversations/${encodeURIComponent(publicId)}/signal`,
      { method: 'POST', body: JSON.stringify({ signal }) },
    ),
  bugReports: () => request<{ reports: BugReportItem[] }>('/admin/bug-reports'),
  bugReport: (publicId: string) =>
    request<{ report: BugReportItem }>(`/admin/bug-reports/${encodeURIComponent(publicId)}`),
  updateBugReport: (
    publicId: string,
    input: {
      status?: BugReportItem['status'];
      linkedConversationPublicId?: string | null;
    },
  ) =>
    request<{
      report: {
        publicId: string;
        status: BugReportItem['status'];
        updatedAt: string;
        diagnosticsDeleted: boolean;
        linkedConversationPublicId?: string | null;
      };
    }>(`/admin/bug-reports/${encodeURIComponent(publicId)}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  health: () =>
    request<{
      status: string;
      checkedAt: string;
      checks: Record<string, string>;
      durationMs: number;
    }>('/admin/system/health'),
  attachmentBlob: async (publicId: string, preview = false) => {
    let response: Response;
    try {
      response = await fetch(
        `${API_BASE_URL}/admin/attachments/${encodeURIComponent(publicId)}/content${preview ? '?preview=1' : ''}`,
        { credentials: 'include' },
      );
    } catch (error) {
      throw new AdminApiFailure(
        'The private attachment is unreachable.',
        'NETWORK_UNAVAILABLE',
        0,
        error,
      );
    }
    if (!response.ok) {
      const payload = (await response.json().catch(() => ({}))) as {
        error?: { code?: string; message?: string; details?: unknown };
      };
      if (response.status === 401) unauthorizedHandler?.();
      throw new AdminApiFailure(
        payload.error?.message ?? 'The safe attachment derivative is unavailable.',
        payload.error?.code ?? 'ATTACHMENT_UNAVAILABLE',
        response.status,
        payload.error?.details,
      );
    }
    return response.blob();
  },
  reportTranscriptError: (publicId: string, note?: string) =>
    request<{ reported: true; reportedAt: string }>(
      `/admin/attachments/${encodeURIComponent(publicId)}/transcript/report`,
      {
        method: 'POST',
        body: JSON.stringify({ ...(note ? { note } : {}) }),
      },
    ),
  redLineStatus: () =>
    request<{
      availabilityState: RedLineAvailabilityState;
      sessionState: RedLineSessionState | null;
      capacity: number;
      activeCount: number;
      openUntil: string | null;
      adminHeartbeatFresh: boolean;
      lastStateChange: string;
      activeSession: {
        conversationPublicId: string;
        sessionState: RedLineSessionState;
        startedAt: string;
        lastActivityAt: string;
        idleWarningSent: boolean;
        priorityPingsUsed: number;
        visitorPresent: boolean;
      } | null;
      blocked: boolean;
    }>('/admin/red-line/status'),
  redLineOpen: (durationSeconds: number) =>
    request<{ availabilityState: 'OPEN'; openUntil: string; capacity: number }>(
      '/admin/red-line/open',
      { method: 'POST', body: JSON.stringify({ durationSeconds }) },
    ),
  redLineClose: (preset?: string) =>
    request<{ closed: true; preset: string }>('/admin/red-line/close', {
      method: 'POST',
      body: JSON.stringify(preset ? { preset } : {}),
    }),
  redLineEndSession: (preset?: string) =>
    request<{ ended: true; endedBy: string; preset: string }>('/admin/red-line/end-session', {
      method: 'POST',
      body: JSON.stringify(preset ? { preset } : {}),
    }),
  redLineHeartbeat: () =>
    request<{ ok: true; heartbeatAt: string }>('/admin/red-line/heartbeat', {
      method: 'POST',
    }),
  redLineBlock: (reason?: string) =>
    request<{ blocked: true; reason: string | null }>('/admin/red-line/block', {
      method: 'POST',
      body: JSON.stringify(reason ? { reason } : {}),
    }),
  redLineUnblock: () => request<{ blocked: false }>('/admin/red-line/unblock', { method: 'POST' }),
  redLineSignal: (reaction: RedLineSignalReaction) =>
    request<{ signal: { reaction: RedLineSignalReaction } }>('/admin/red-line/signal', {
      method: 'POST',
      body: JSON.stringify({ reaction }),
    }),
  redLineConvert: () =>
    request<{ converted: true; convertedBy: string }>('/admin/red-line/convert', {
      method: 'POST',
    }),
  redLineMessage: (message: string, clientMessageId: string) =>
    request<{ message: { publicId: string; duplicate: boolean } }>('/admin/red-line/messages', {
      method: 'POST',
      body: JSON.stringify({ message, clientMessageId }),
    }),
  redLineMessages: () => request<RedLineMessagesResponse>('/admin/red-line/messages'),
  redLineRealtimeTicket: () =>
    request<{ realtime: RealtimeTicketResponse }>('/admin/red-line/realtime-ticket', {
      method: 'POST',
    }),
  signalPortalConfig: (projectId: string) =>
    request<{ config: SignalPortalConfig }>(
      `/admin/signal-portal/config/${encodeURIComponent(projectId)}`,
    ),
  signalPortalUpdateConfig: (projectId: string, update: SignalPortalConfigUpdate) =>
    request<{ config: SignalPortalConfig }>(
      `/admin/signal-portal/config/${encodeURIComponent(projectId)}`,
      { method: 'PUT', body: JSON.stringify(update) },
    ),
  signalPortalStatus: () =>
    request<{ publicStatus: string; invisible: boolean }>('/admin/signal-portal/status'),
  signalPortalUpdateStatus: (update: SignalPortalStatusUpdate) =>
    request<{ publicStatus: string }>('/admin/signal-portal/status', {
      method: 'PUT',
      body: JSON.stringify(update),
    }),
};
